import { createApi } from '@reduxjs/toolkit/query/react';
import { GuidT, PaginationIF } from '@/types/common';
import { EVENTS_API_TAGS } from '@/api/eventsApi/config';
import { eventsApi } from '@/api/eventsApi/eventsApi';
import {
    OutfitModelIF,
    SearchOutfitsIF,
    CreateOutfitIF,
    DeleteOutfitIF,
} from '@/api/outfitsApi/types';
import { fetchRestApiQuery } from '@/helpers/api';

export const outfitsApi = createApi({
    reducerPath: 'outfitsApi',
    baseQuery: fetchRestApiQuery('/Outfits'),
    tagTypes: ['SearchOutfits', 'GetOutfitById', 'Suitcases'],
    endpoints: (builder) => ({
        searchOutfits: builder.query<
            PaginationIF<OutfitModelIF>,
            SearchOutfitsIF
        >({
            query: (parameters) => {
                const searchParameters = new URLSearchParams();

                parameters.guids?.forEach((id) =>
                    searchParameters.append('Guids', id),
                );

                if (parameters.userId) {
                    searchParameters.append('UserGuid', parameters.userId);
                }

                if (parameters.text) {
                    searchParameters.append('Text', parameters.text);
                }

                if (parameters.occasion) {
                    searchParameters.append('Occasion', parameters.occasion);
                }

                if (parameters.season) {
                    searchParameters.append('Season', parameters.season);
                }

                if (parameters.targetGender) {
                    searchParameters.append(
                        'TargetGender',
                        parameters.targetGender,
                    );
                }

                if (parameters.pageSize) {
                    searchParameters.append(
                        'PageSize',
                        String(parameters.pageSize),
                    );
                }

                if (parameters.page) {
                    searchParameters.append('Page', String(parameters.page));
                }

                if (parameters.sortMember) {
                    searchParameters.append(
                        'SortMember',
                        parameters.sortMember,
                    );
                }

                if (parameters.ascending !== undefined) {
                    searchParameters.append(
                        'Ascending',
                        String(parameters.ascending),
                    );
                }

                return {
                    url: `?${searchParameters.toString()}`,
                };
            },
            providesTags: ['SearchOutfits'],
        }),
        getOutfitById: builder.query<OutfitModelIF, GuidT>({
            query: (id) => `/${id}`,
            providesTags: (_result, _error, id) => [
                { type: 'SearchOutfits', id },
            ],
        }),
        createOutfit: builder.mutation<OutfitModelIF, CreateOutfitIF>({
            query: (body) => ({
                url: '',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SearchOutfits'],
        }),
        updateOutfit: builder.mutation<
            boolean,
            { id: GuidT; body: CreateOutfitIF }
        >({
            query: ({ id, body }) => ({
                url: `/${id}`,
                method: 'PUT',
                body,
            }),

            async onQueryStarted(
                _updateOutfitData,
                { dispatch, queryFulfilled },
            ) {
                await queryFulfilled;

                dispatch(
                    outfitsApi.util.invalidateTags([
                        'SearchOutfits',
                        'GetOutfitById',
                        'Suitcases',
                    ]),
                );
                dispatch(eventsApi.util.invalidateTags(EVENTS_API_TAGS));
            },
            transformResponse() {
                return true;
            },
            transformErrorResponse() {
                return false;
            },
        }),
        deleteOutfit: builder.mutation<void, DeleteOutfitIF>({
            query: ({ outfitGuid, force }) => ({
                url: `/${outfitGuid}`,
                method: 'DELETE',
                params: force ? { Force: true } : undefined,
            }),
            async onQueryStarted(_outfitGuid, { dispatch, queryFulfilled }) {
                await queryFulfilled;

                dispatch(
                    outfitsApi.util.invalidateTags([
                        'SearchOutfits',
                        'Suitcases',
                    ]),
                );
                dispatch(eventsApi.util.invalidateTags(EVENTS_API_TAGS));
            },
        }),
        generateRandom: builder.mutation<OutfitModelIF, void>({
            query: () => ({
                url: '/GenerateRandom',
                method: 'POST',
            }),
            invalidatesTags: ['SearchOutfits'],
        }),
    }),
});
